/** BullMQ queue names. Keep in sync with the workers in apps/server/src/modules/queues. */
export const QUEUE = {
  tgIngest: "tg-ingest",
  parse: "parse",
  decide: "decide",
  execute: "execute",
  manage: "manage",
  notify: "notify",
  push: "push",
  reports: "reports",
  reconcile: "reconcile",
  lifecycle: "lifecycle",
} as const;

export type QueueName = (typeof QUEUE)[keyof typeof QUEUE];

/**
 * All trade-mutating work funnels through `execute` with concurrency 1, so MetaApi
 * calls are globally serialized. Nothing else may call a trade method directly.
 */
export const EXECUTE_CONCURRENCY = 1;

/**
 * BullMQ rejects a custom job id containing ":" outright ("Custom Id cannot contain :").
 * Every deterministic job id in this codebase goes through here so that constraint is
 * enforced in one place rather than rediscovered per queue.
 *
 * Note this applies only to BullMQ job ids. Database dedupe keys (`metaapi_events.
 * dedupe_key` and friends) are free to use colons and do.
 */
export function jobId(...parts: Array<string | number>): string {
  const id = parts.map(String).join("|");
  if (id.includes(":")) {
    throw new Error(`BullMQ job id must not contain ":" — got ${JSON.stringify(id)}`);
  }
  return id;
}

/** WebSocket topics pushed to the PWA. `quote:` is a prefix — clients subscribe per symbol. */
export const WS_TOPIC = {
  positions: "positions",
  account: "account",
  signal: "signal",
  notification: "notification",
  health: "health",
  quotePrefix: "quote:",
} as const;

/** app_settings keys. Values are validated by the matching schema in ./schemas/settings.ts */
export const SETTING = {
  killSwitch: "kill_switch",
  dryRun: "dry_run",
  lotPolicy: "lot_policy",
  marketThresholdPips: "market_threshold_pips",
  priceSanityPct: "price_sanity_pct",
  parseConfidenceMin: "parse_confidence_min",
  decideConfidenceMin: "decide_confidence_min",
  signalMaxAgeMinutes: "signal_max_age_minutes",
  weekendSchedule: "weekend_schedule",
  exposureCaps: "exposure_caps",
  models: "models",
  dailyLlmBudgetUsd: "daily_llm_budget_usd",
  creditThresholds: "credit_thresholds",
  openaiCreditBaseline: "openai_credit_baseline",
  notificationDelivery: "notification_delivery",
} as const;

export type SettingKey = (typeof SETTING)[keyof typeof SETTING];

/**
 * MetaApi packs `clientId` and `comment` into the single MT4 order comment field and
 * caps their combined length. We spend the whole budget on clientId and always send an
 * empty comment. Verify against the installed SDK typings before raising.
 * @see https://metaapi.cloud/docs/client/clientIdUsage/
 */
export const CLIENT_ID_MAX_LEN = 26;
